import Button from '@rio-cloud/rio-uikit/Button';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import { CSVLink } from 'react-csv';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { getUserAccount } from '../../../../configuration/login/loginSlice';
import { useGetAssociationsQuery, useGetTagsQuery } from '../../../services/assetAdministrationApi';
import { getAssetAssociatedDeviceMap } from '../../reducers/assets/assetsSliceUtils';
import { type AssetExportColumnKey, getDataForCsvLink, getVisibleExportColumnKeys } from './CsvExport.helper';
import type { Asset, TableColumnDetails } from './types';

export const CsvExport = (props: { assets: Asset[]; columns: TableColumnDetails[] }) => {
    const accountId = useAppSelector(getUserAccount) ?? null;
    const { data: availableTags = [] } = useGetTagsQuery(accountId);
    const { data: associations = [] } = useGetAssociationsQuery();
    const devices = Object.fromEntries(
        Object.entries(getAssetAssociatedDeviceMap(associations)).map(([assetId, associatedDevices]) => [
            assetId,
            associatedDevices.map(associatedDevice => associatedDevice.device),
        ])
    );

    const intl = useIntl();
    const exportColumnKeys = getVisibleExportColumnKeys(props.columns);
    const columnLabelsByKey = {
        name: intl.formatMessage({ id: 'intl-msg:asset.name' }),
        identification: intl.formatMessage({ id: 'intl-msg:asset.identification_type.vin' }),
        license_plate: intl.formatMessage({ id: 'intl-msg:asset.license-plate' }),
        brand: intl.formatMessage({ id: 'intl-msg:asset.brand' }),
        fuel_type: intl.formatMessage({ id: 'intl-msg:masterdata.fuel-type' }),
        type: intl.formatMessage({ id: 'intl-msg:asset.type' }),
        status: intl.formatMessage({ id: 'intl-msg:asset.status' }),
        tags: intl.formatMessage({ id: 'intl-msg:asset.tags' }),
        devices: intl.formatMessage({ id: 'intl-msg:asset.devices' }),
    } satisfies Record<AssetExportColumnKey, string>;

    const headers = exportColumnKeys.map(columnKey => ({
        label: columnLabelsByKey[columnKey],
        key: columnKey,
    }));

    const data = getDataForCsvLink(props.assets, availableTags, devices);

    return (
        <CSVLink
            filename={'RIO-Assets.csv'}
            data={data}
            headers={headers}
            {...getTrackingAttributes({
                trigger: 'click',
                category: 'link autoTracking',
                action: 'link click',
                label: 'intl-msg:csv-export',
            })}
        >
            <Button bsStyle={Button.DEFAULT} iconName='rioglyph-download'>
                <FormattedMessage id={'intl-msg:csv-export'} />
            </Button>
        </CSVLink>
    );
};
